/* eslint-disable no-undef */

/**
 * ProductGalleryVideo — carousel / accordion longform video, backed by Media Chrome.
 *
 * Media Chrome (loaded via media-chrome.js) owns all playback UI: play/pause,
 * mute, fullscreen, scrubbing and autohide. This element is a thin adapter that:
 *  - preserves the public API the carousel controller (carousel-product-media.js)
 *    relies on (pauseForSlideChange / resumeForSlideChange / loadVideo / playVideo /
 *    pauseVideo / videoLoaded / isLoading / isPlaying / hasBeenPlayed / wasPausedByUser),
 *  - tracks the small amount of state the carousel reads, and
 *  - guards Embla swipes while the user scrubs (data-progress-dragging).
 *
 * Deferred loading is native via <video preload="none"> — no download until play.
 */
class ProductGalleryVideo extends HTMLElement {
  constructor () {
    super()
    this._video = null
    this._mediaController = null
    this._hasBeenPlayed = false
    this._wasPausedByUser = false
    this._userPauseIntent = false

    // Compatibility shim: carousel-product-media.js pokes
    // videoPlayer.{isPlaying, stopProgressUpdate, showPlayState}.
    this.videoPlayer = {
      isPlaying: false,
      stopProgressUpdate () {},
      startProgressUpdate () {},
      showPlayState () {},
      showPauseState () {},
    }
  }

  connectedCallback () {
    this.setAttribute('tabindex', '0')

    this._mediaController = this.querySelector('media-controller')
    this._video = this.querySelector('video')
    this._hasBeenPlayed = this.getAttribute('data-has-been-played') === 'true'
    this._wasPausedByUser = this.getAttribute('data-was-paused-by-user') === 'true'

    if (!this._video || !this._mediaController) return

    this._onPlay = () => {
      this._hasBeenPlayed = true
      this._wasPausedByUser = false
      this.videoPlayer.isPlaying = true
      this.setAttribute('data-has-been-played', 'true')
      this.setAttribute('data-was-paused-by-user', 'false')
      this.classList.remove('video-not-started')
    }
    this._onPause = () => {
      this.videoPlayer.isPlaying = false
      // Only a pause the user actually asked for (via the Media Chrome control)
      // counts as "paused by user". Slide-change / carousel pauses are programmatic
      // and must not suppress auto-resume.
      if (this._userPauseIntent) {
        this._wasPausedByUser = true
        this.setAttribute('data-was-paused-by-user', 'true')
      }
      this._userPauseIntent = false
    }
    this._onEnded = () => {
      this.videoPlayer.isPlaying = false
    }

    this._video.addEventListener('play', this._onPlay)
    this._video.addEventListener('pause', this._onPause)
    this._video.addEventListener('ended', this._onEnded)

    // Media Chrome request events tell us a play/pause was user-driven.
    this._onPauseRequest = () => { this._userPauseIntent = true }
    this._onPlayRequest = () => {
      this._userPauseIntent = false
      this._wasPausedByUser = false
      this.setAttribute('data-was-paused-by-user', 'false')
    }
    this._mediaController.addEventListener('mediapauserequest', this._onPauseRequest)
    this._mediaController.addEventListener('mediaplayrequest', this._onPlayRequest)

    this._setupScrubberDragGuard()
  }

  disconnectedCallback () {
    if (this._video) {
      this._video.removeEventListener('play', this._onPlay)
      this._video.removeEventListener('pause', this._onPause)
      this._video.removeEventListener('ended', this._onEnded)
    }
    if (this._mediaController) {
      this._mediaController.removeEventListener('mediapauserequest', this._onPauseRequest)
      this._mediaController.removeEventListener('mediaplayrequest', this._onPlayRequest)
    }
    window.removeEventListener('pointerup', this._onRangeUp)
    window.removeEventListener('pointercancel', this._onRangeUp)
  }

  _setupScrubberDragGuard () {
    const range = this.querySelector('media-time-range')
    if (!range) return
    this._onRangeDown = () => { this.setAttribute('data-progress-dragging', 'true') }
    this._onRangeUp = () => {
      // Defer so Embla's watchDrag still sees the flag on the final pointerup.
      window.requestAnimationFrame(() => this.removeAttribute('data-progress-dragging'))
    }
    range.addEventListener('pointerdown', this._onRangeDown)
    window.addEventListener('pointerup', this._onRangeUp)
    window.addEventListener('pointercancel', this._onRangeUp)
  }

  _pause ({ userIntent = false } = {}) {
    if (!this._video || this._video.paused) return
    this._userPauseIntent = userIntent
    this._video.pause()
  }

  /* -------- Public API used by carousel-product-media.js -------- */
  pauseForSlideChange () {
    this._pause({ userIntent: false })
  }

  resumeForSlideChange () {
    if (this._hasBeenPlayed && !this._wasPausedByUser && this._video &&
        this._video.paused && this._video.readyState >= 2) {
      this._video.play().catch(() => {})
    }
  }

  resetVideo () {
    if (!this._video) return
    this._pause({ userIntent: false })
    this._video.currentTime = 0
    this._wasPausedByUser = false
    this.setAttribute('data-was-paused-by-user', 'false')
  }

  loadVideo () {
    // Deferred loading is native (preload="none"). Bump preload so an active
    // slide starts buffering ahead of an explicit play.
    if (this._video && this._video.preload === 'none') {
      this._video.preload = 'metadata'
      this._video.load()
    }
  }

  playVideo () {
    return this._video ? this._video.play().catch(() => {}) : undefined
  }

  pauseVideo () {
    this._pause({ userIntent: true })
  }

  get videoLoaded () {
    return this._video ? this._video.readyState >= 2 : false
  }

  get isLoading () {
    // HAVE_NOTHING/HAVE_METADATA while the network is actively loading.
    return this._video ? (this._video.readyState < 2 && this._video.networkState === 2) : false
  }

  get isPlaying () {
    return this._video ? !this._video.paused : false
  }

  get hasBeenPlayed () {
    return this._hasBeenPlayed
  }

  get wasPausedByUser () {
    return this._wasPausedByUser
  }

  // No-ops kept for API compatibility (Media Chrome renders playback state).
  showPlayState () {}
  showPauseState () {}
  showLoadingState () {}
}

if (!customElements.get('product-gallery-video')) {
  customElements.define('product-gallery-video', ProductGalleryVideo)
}
